/**
 * streaming-download.service.ts
 *
 * INFRA-ADR-023 D5 / SRS-008 FR-3 / SDS-008 §5
 *
 * 大檔案(≥ 2GB)下載改用 File System Access API + ReadableStream
 * 直寫使用者磁碟,避免 browser tab 在 blob 階段爆記憶體。
 *
 * Fallback 策略:
 *   < 2GB           → 維持既有 blob + a.click()(小檔快速體驗)
 *   ≥ 2GB, Chrome   → 走此 service(showSaveFilePicker)
 *   ≥ 2GB, 不支援   → 顯示提示訊息,建議改用 Chrome 或 file-watcher agent restore
 *
 * 安全考量:
 *   - 使用者每次下載都需透過 picker 授權(browser 強制,不持久化 handle)
 *   - 只對 /api/v1/files/:id/download 這個 endpoint 生效
 */

/** 閾值:超過改走 streaming path */
export const LARGE_FILE_THRESHOLD_BYTES = 2 * 1024 * 1024 * 1024; // 2 GiB

/** Browser 支援檢測 */
export function isFileSystemAccessSupported(): boolean {
  return typeof (window as unknown as { showSaveFilePicker?: unknown }).showSaveFilePicker === 'function';
}

interface StreamingDownloadOptions {
  fileId: string;
  fileName: string;
  fileSize: number;
  /** JWT token — 透過 Authorization header 傳給 backend */
  token: string;
  /** 進度回報 (bytesWritten, totalBytes) */
  onProgress?: (bytesWritten: number, totalBytes: number) => void;
  /** 取消 signal */
  signal?: AbortSignal;
}

interface StreamingDownloadResult {
  /** 實際使用的下載路徑: blob / fs-access / unsupported */
  method: 'blob' | 'fs-access' | 'unsupported';
  /** 寫入使用者磁碟的 bytes(unsupported 時為 0) */
  bytesWritten: number;
}

/**
 * downloadFileStreaming — 主入口
 *
 * 回 method=unsupported 的情境下,caller 應該顯示「請用 Chrome 或 agent」訊息。
 */
export async function downloadFileStreaming(
  baseUrl: string,
  opts: StreamingDownloadOptions
): Promise<StreamingDownloadResult> {
  const isLarge = opts.fileSize >= LARGE_FILE_THRESHOLD_BYTES;

  if (!isLarge) {
    // 走既有 blob 路徑(不在此 service 實作,由 caller 走舊 code)
    return { method: 'blob', bytesWritten: 0 };
  }

  if (!isFileSystemAccessSupported()) {
    return { method: 'unsupported', bytesWritten: 0 };
  }

  // === Large file:走 FS Access API + streaming fetch ===
  const handle = await (window as unknown as {
    showSaveFilePicker: (options: {
      suggestedName: string;
    }) => Promise<FileSystemFileHandleLike>;
  }).showSaveFilePicker({ suggestedName: opts.fileName });

  const writable = await handle.createWritable();

  try {
    const response = await fetch(`${baseUrl}/api/v1/files/${opts.fileId}/download`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${opts.token}`,
        Accept: 'application/octet-stream',
      },
      signal: opts.signal,
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }
    if (!response.body) {
      throw new Error('Response has no body (streaming unsupported by this server?)');
    }

    const reader = response.body.getReader();
    let bytesWritten = 0;

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value && value.byteLength > 0) {
        await writable.write(value);
        bytesWritten += value.byteLength;
        if (opts.onProgress) {
          opts.onProgress(bytesWritten, opts.fileSize);
        }
      }
    }

    await writable.close();
    return { method: 'fs-access', bytesWritten };
  } catch (err) {
    // 確保 writable handle 被釋放,即使失敗
    try {
      await writable.abort();
    } catch {
      /* ignore secondary error */
    }
    throw err;
  }
}

// ---- minimal types for TS strict mode when lib.dom.d.ts lacks FS Access API ----

interface FileSystemFileHandleLike {
  createWritable(): Promise<FileSystemWritableFileStreamLike>;
}

interface FileSystemWritableFileStreamLike {
  write(chunk: BufferSource): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}
